import zionDebug from '@fs/zion-debug'

const debug = zionDebug('standards:places:apiService')

// Array of all place permissions to check
export const ALL_PLACE_PERMISSIONS = [
  'PERM_ALT_JURISDICTION_CREATE',
  'PERM_ALT_JURISDICTION_MODIFY',
  'PERM_ATTRIBUTE_CREATE',
  'PERM_ATTRIBUTE_MODIFY',
  'PERM_ATTR_COPYRIGHT_CREATE',
  'PERM_ATTR_COPYRIGHT_DELETE',
  'PERM_BOUNDARY_CREATE',
  'PERM_BOUNDARY_MODIFY',
  'PERM_CITATION_CREATE',
  'PERM_CITATION_MODIFY',
  'PERM_EXT_XREF_CREATE',
  'PERM_EXT_XREF_MODIFY',
  'PERM_PLACES_ADMIN_APP',
  'PERM_PLACE_DATES',
  'PERM_PLACE_VARIANT_CREATE',
  'PERM_PLACE_VARIANT_MODIFY',
  'PERM_REP_CERTIFIED_TO_CONFIRMED',
  'PERM_REP_CHILD_CONSTRAINT',
  'PERM_REP_CONFIRMED_TO_CERTIFIED',
  'PERM_REP_CONFIRMED_TO_PROVISIONAL',
  'PERM_REP_COORDINATES',
  'PERM_REP_CREATE_CONFIRMED',
  'PERM_REP_CREATE_PROVISIONAL',
  'PERM_REP_DELETE',
  'PERM_REP_DELETE_CONFIRMED',
  'PERM_REP_DELETE_PROVISIONAL',
  'PERM_REP_DISPLAY_NAME_CREATE',
  'PERM_REP_DISPLAY_NAME_MODIFY',
  'PERM_REP_FROM_TO_YEAR',
  'PERM_REP_GROUP',
  'PERM_REP_GROUP_MEMBER',
  'PERM_REP_PARENT',
  'PERM_REP_PLACE_TYPE',
  'PERM_REP_PREFERRED_BOUNDARY',
  'PERM_REP_PREFERRED_LOCALE',
  'PERM_REP_PROVISIONAL_TO_CONFIRMED',
  'PERM_SOURCE_CREATE',
  'PERM_SOURCE_DELETE',
  'PERM_SOURCE_UPDATE',
  'PERM_TOP_LEVEL_SEARCH',
  'PERM_TYPE_AND_TERM',
  'PERM_TYPE_GROUP',
  'PERM_TYPE_GROUP_MEMBER',
] as const

export const PLACE_PERMISSIONS_MAP = {
  CAN_EDIT: {
    // Place editing permissions
    PERM_REP_CREATE_PROVISIONAL: 'PERM_REP_CREATE_PROVISIONAL', // create new provisional place rep
    PERM_REP_CREATE_CONFIRMED: 'PERM_REP_CREATE_CONFIRMED', // create new confirmed place rep
    PERM_REP_DISPLAY_NAME_CREATE: 'PERM_REP_DISPLAY_NAME_CREATE', // create display name
    PERM_REP_PARENT: 'PERM_REP_PARENT', // assign/update parent place
    PERM_REP_PLACE_TYPE: 'PERM_REP_PLACE_TYPE', // assign/update place type
    PERM_REP_FROM_TO_YEAR: 'PERM_REP_FROM_TO_YEAR', // set/edit start/end years
    PERM_REP_COORDINATES: 'PERM_REP_COORDINATES', // set/update coordinates
    PERM_REP_PREFERRED_LOCALE: 'PERM_REP_PREFERRED_LOCALE', // set/update the native spoken language
    PERM_REP_DISPLAY_NAME_MODIFY: 'PERM_REP_DISPLAY_NAME_MODIFY', // modify display name settings
    // Attribute/fact editing permissions
    PERM_ATTRIBUTE_CREATE: 'PERM_ATTRIBUTE_CREATE', // create new attributes/facts/historical info
    PERM_ATTRIBUTE_MODIFY: 'PERM_ATTRIBUTE_MODIFY', // modify existing attributes/facts/historical info
    PERM_ATTR_COPYRIGHT_CREATE: 'PERM_ATTR_COPYRIGHT_CREATE', // create copyright attributes
    PERM_ATTR_COPYRIGHT_DELETE: 'PERM_ATTR_COPYRIGHT_DELETE', // delete copyright attributes
    // Name/variant editing permissions
    PERM_PLACE_VARIANT_CREATE: 'PERM_PLACE_VARIANT_CREATE', // add new place names/variants
    PERM_PLACE_VARIANT_MODIFY: 'PERM_PLACE_VARIANT_MODIFY', // edit/delete place names/variants
    // Citation editing permissions
    PERM_CITATION_CREATE: 'PERM_CITATION_CREATE', // create new citations
    PERM_CITATION_MODIFY: 'PERM_CITATION_MODIFY', // modify/delete existing citations
    // Related place editing permissions
    PERM_ALT_JURISDICTION_CREATE: 'PERM_ALT_JURISDICTION_CREATE', // add related places
    PERM_ALT_JURISDICTION_MODIFY: 'PERM_ALT_JURISDICTION_MODIFY', // modify/delete related places
    // Delete permissions
    PERM_REP_DELETE: 'PERM_REP_DELETE', // delete a place rep
    PERM_REP_DELETE_PROVISIONAL: 'PERM_REP_DELETE_PROVISIONAL', // delete provisional rep
    PERM_REP_DELETE_CONFIRMED: 'PERM_REP_DELETE_CONFIRMED', // delete confirmed rep
    // Certification permissions
    PERM_REP_PROVISIONAL_TO_CONFIRMED: 'PERM_REP_PROVISIONAL_TO_CONFIRMED', // change provisional to confirmed status
    PERM_REP_CONFIRMED_TO_PROVISIONAL: 'PERM_REP_CONFIRMED_TO_PROVISIONAL', // change confirmed to provisional status
    PERM_REP_CONFIRMED_TO_CERTIFIED: 'PERM_REP_CONFIRMED_TO_CERTIFIED', // change confirmed to certified status
    PERM_REP_CERTIFIED_TO_CONFIRMED: 'PERM_REP_CERTIFIED_TO_CONFIRMED', // change certified to confirmed status
  },
  ADD_PLACE: {
    PERM_REP_CREATE_PROVISIONAL: 'PERM_REP_CREATE_PROVISIONAL', // create new provisional place rep
    PERM_REP_CREATE_CONFIRMED: 'PERM_REP_CREATE_CONFIRMED', // create new confirmed place rep
    PERM_REP_DISPLAY_NAME_CREATE: 'PERM_REP_DISPLAY_NAME_CREATE', // create display name
    PERM_REP_PARENT: 'PERM_REP_PARENT', // assign parent place
    PERM_REP_PLACE_TYPE: 'PERM_REP_PLACE_TYPE', // assign place type
    PERM_REP_FROM_TO_YEAR: 'PERM_REP_FROM_TO_YEAR', // set start/end years
    PERM_REP_COORDINATES: 'PERM_REP_COORDINATES', // set/update coordinates
    PERM_REP_PREFERRED_LOCALE: 'PERM_REP_PREFERRED_LOCALE', // set/update the native spoken language
  },
  EDIT_PLACE: {
    PERM_REP_PARENT: 'PERM_REP_PARENT', // update parent place
    PERM_REP_PLACE_TYPE: 'PERM_REP_PLACE_TYPE', // update place type
    PERM_REP_FROM_TO_YEAR: 'PERM_REP_FROM_TO_YEAR', // edit start/end years
    PERM_REP_COORDINATES: 'PERM_REP_COORDINATES', // update coordinates
    PERM_REP_PREFERRED_LOCALE: 'PERM_REP_PREFERRED_LOCALE', // update the native spoken language
  },
  DELETE_PLACE: {
    PERM_REP_DELETE: 'PERM_REP_DELETE', // delete a place rep
    PERM_REP_DELETE_PROVISIONAL: 'PERM_REP_DELETE_PROVISIONAL', // delete provisional rep
    PERM_REP_DELETE_CONFIRMED: 'PERM_REP_DELETE_CONFIRMED', // delete confirmed rep
    PERM_REP_PARENT: 'PERM_REP_PARENT', // may be required for redirect target in delete/merge
  },
  CERTIFICATION: {
    PERM_REP_PROVISIONAL_TO_CONFIRMED: 'PERM_REP_PROVISIONAL_TO_CONFIRMED', // change provisional to confirmed status
    PERM_REP_CONFIRMED_TO_PROVISIONAL: 'PERM_REP_CONFIRMED_TO_PROVISIONAL', // change confirmed to provisional status - accepted in frontend
    PERM_REP_CONFIRMED_TO_CERTIFIED: 'PERM_REP_CONFIRMED_TO_CERTIFIED', // change confirmed to certified status
    PERM_REP_CERTIFIED_TO_CONFIRMED: 'PERM_REP_CERTIFIED_TO_CONFIRMED', // change certified to confirmed status
  },
  ADD_HISTORICAL_PERIOD: {
    PERM_REP_CREATE_PROVISIONAL: 'PERM_REP_CREATE_PROVISIONAL', // create new provisional place rep
    PERM_REP_CREATE_CONFIRMED: 'PERM_REP_CREATE_CONFIRMED', // create new confirmed place rep
    PERM_REP_DISPLAY_NAME_CREATE: 'PERM_REP_DISPLAY_NAME_CREATE', // create display name
    PERM_REP_PARENT: 'PERM_REP_PARENT', // assign parent place
    PERM_REP_PLACE_TYPE: 'PERM_REP_PLACE_TYPE', // assign place type
    PERM_ATTRIBUTE_CREATE: 'PERM_ATTRIBUTE_CREATE', // set native spoken language
    PERM_REP_FROM_TO_YEAR: 'PERM_REP_FROM_TO_YEAR', // set start/end years
    PERM_REP_COORDINATES: 'PERM_REP_COORDINATES', // set coordinates
    PERM_REP_PREFERRED_LOCALE: 'PERM_REP_PREFERRED_LOCALE', // set the native spoken language
  },
  ADD_FACT: {
    PERM_ATTRIBUTE_CREATE: 'PERM_ATTRIBUTE_CREATE', // create new attributes/facts
    PERM_ATTR_COPYRIGHT_CREATE: 'PERM_ATTR_COPYRIGHT_CREATE', // create copyright attributes
  },
  EDIT_FACT: {
    PERM_ATTRIBUTE_MODIFY: 'PERM_ATTRIBUTE_MODIFY', // modify existing attributes/facts
    PERM_ATTR_COPYRIGHT_CREATE: 'PERM_ATTR_COPYRIGHT_CREATE', // create copyright attributes
    // PERM_ATTR_COPYRIGHT_DELETE: 'PERM_ATTR_COPYRIGHT_DELETE', // delete copyright attributes
  },
  DELETE_FACT: {
    PERM_ATTRIBUTE_MODIFY: 'PERM_ATTRIBUTE_MODIFY', // modify existing attributes/facts
    // PERM_ATTR_COPYRIGHT_DELETE: 'PERM_ATTR_COPYRIGHT_DELETE', // delete copyright attributes
  },
  ADD_HISTORICAL_INFORMATION: {
    PERM_ATTRIBUTE_CREATE: 'PERM_ATTRIBUTE_CREATE', // create a new Historical Info entry (type, language, value, year)
    PERM_ATTR_COPYRIGHT_CREATE: 'PERM_ATTR_COPYRIGHT_CREATE', // set the "Copyrighted Information" flag
  },
  EDIT_HISTORICAL_INFORMATION: {
    PERM_ATTRIBUTE_MODIFY: 'PERM_ATTRIBUTE_MODIFY', // edit or delete an existing entry
    PERM_ATTR_COPYRIGHT_CREATE: 'PERM_ATTR_COPYRIGHT_CREATE', // set the "Copyrighted Information" flag
    // PERM_ATTR_COPYRIGHT_DELETE: 'PERM_ATTR_COPYRIGHT_DELETE', // clear the "Copyrighted Information" flag
  },
  DELETE_HISTORICAL_INFORMATION: {
    PERM_ATTRIBUTE_MODIFY: 'PERM_ATTRIBUTE_MODIFY', // modify existing attributes/facts
    // PERM_ATTR_COPYRIGHT_DELETE: 'PERM_ATTR_COPYRIGHT_DELETE', // delete copyright attributes
  },
  ADD_RESEARCH_LINK: {
    PERM_ATTRIBUTE_CREATE: 'PERM_ATTRIBUTE_CREATE', // create new research link attributes
    PERM_CITATION_CREATE: 'PERM_CITATION_CREATE', // create new research link citations
  },
  EDIT_RESEARCH_LINK: {
    PERM_ATTRIBUTE_MODIFY: 'PERM_ATTRIBUTE_MODIFY', // modify existing research link attributes
    PERM_CITATION_MODIFY: 'PERM_CITATION_MODIFY', // modify existing research link citations
  },
  DELETE_RESEARCH_LINK: {
    PERM_ATTRIBUTE_MODIFY: 'PERM_ATTRIBUTE_MODIFY', // modify existing research link attributes
    PERM_CITATION_MODIFY: 'PERM_CITATION_MODIFY', // delete a research link stored as a citation
  },
  ADD_RELATED_PLACE: {
    PERM_ALT_JURISDICTION_CREATE: 'PERM_ALT_JURISDICTION_CREATE', // add a related place (new relationship)
  },
  EDIT_RELATED_PLACE: {
    PERM_ALT_JURISDICTION_MODIFY: 'PERM_ALT_JURISDICTION_MODIFY', // edit a related place (modify relationship)
  },
  DELETE_RELATED_PLACE: {
    PERM_ALT_JURISDICTION_MODIFY: 'PERM_ALT_JURISDICTION_MODIFY', // delete a related place (remove relationship link)
  },
  ADD_CITATION: {
    PERM_CITATION_CREATE: 'PERM_CITATION_CREATE', // add a new citation
  },
  EDIT_CITATION: {
    PERM_CITATION_MODIFY: 'PERM_CITATION_MODIFY', // edit or delete an existing citation
  },
  DELETE_CITATION: {
    PERM_CITATION_MODIFY: 'PERM_CITATION_MODIFY', // edit or delete an existing citation
  },
  ADD_NAME: {
    PERM_PLACE_VARIANT_CREATE: 'PERM_PLACE_VARIANT_CREATE', // add a new name via + ADD NAME (creates a name/variant for the rep)
    PERM_REP_DISPLAY_NAME_MODIFY: 'PERM_REP_DISPLAY_NAME_MODIFY', // toggle the Display Name checkbox to set which name is the primary display name
  },
  EDIT_NAME: {
    PERM_PLACE_VARIANT_MODIFY: 'PERM_PLACE_VARIANT_MODIFY', // edit/update the name value, language, or type
    PERM_REP_DISPLAY_NAME_MODIFY: 'PERM_REP_DISPLAY_NAME_MODIFY', // required if the name is the designated display name (to cascade changes)
  },
  DELETE_NAME: {
    PERM_PLACE_VARIANT_MODIFY: 'PERM_PLACE_VARIANT_MODIFY', // delete a place name (variant)
    PERM_REP_DISPLAY_NAME_MODIFY: 'PERM_REP_DISPLAY_NAME_MODIFY', // also required if the deleted name is the current display name
  },
} as const

type PermissionsMap = Record<string, boolean>

type Health = {
  canWrite?: boolean
}

/**
 * Check if user has ALL permissions required for a specific feature
 * @param permissions - The permissions object from getPlacePermissions()
 * @param featureKey - The key from PLACE_PERMISSIONS_MAP (e.g., 'ADD_PLACE', 'EDIT_FACT')
 * @param health - Health object with canWrite property, optional for permission checks
 * @return True if user has all required permissions (and canWrite if health provided)
 */
export function hasAllPermissionsForFeature(
  permissions: PermissionsMap,
  featureKey: string,
  health: Health | null = null
): boolean {
  if (!permissions || typeof permissions !== 'object') {
    return false
  }

  const featurePermissions = PLACE_PERMISSIONS_MAP[featureKey as keyof typeof PLACE_PERMISSIONS_MAP]
  if (!featurePermissions) {
    debug(`Feature '${featureKey}' not found in PLACE_PERMISSIONS_MAP`)
    return false
  }

  const requiredPermissions = Object.values(featurePermissions)
  const hasAllPerms = requiredPermissions.every((perm) => permissions[perm] === true)

  if (health) {
    return hasAllPerms && health.canWrite === true
  }

  return hasAllPerms
}

/**
 * Check if user has ANY permission required for a specific feature
 * @param permissions - The permissions object from getPlacePermissions()
 * @param featureKey - The key from PLACE_PERMISSIONS_MAP (e.g., 'ADD_PLACE', 'EDIT_FACT')
 * @param health - Health object with canWrite property, optional for permission checks
 * @return True if user has at least one required permission (and canWrite if health provided)
 */
export function hasAnyPermissionForFeature(
  permissions: PermissionsMap,
  featureKey: string,
  health: Health | null = null
): boolean {
  if (!permissions || typeof permissions !== 'object') {
    return false
  }

  const featurePermissions = PLACE_PERMISSIONS_MAP[featureKey as keyof typeof PLACE_PERMISSIONS_MAP]
  if (!featurePermissions) {
    debug(`Feature '${featureKey}' not found in PLACE_PERMISSIONS_MAP`)
    return false
  }

  const requiredPermissions = Object.values(featurePermissions)
  const hasAnyPerm = requiredPermissions.some((perm) => permissions[perm] === true)

  if (health) {
    return hasAnyPerm && health.canWrite === true
  }

  return hasAnyPerm
}

/**
 * Check if user has a specific permission
 * @param permissions - The permissions object from getPlacePermissions()
 * @param permissionKey - The specific permission to check (e.g., 'PERM_ATTRIBUTE_MODIFY')
 * @param health - Health object with canWrite property, optional for permission checks
 * @return True if user has the permission (and canWrite if health provided)
 */
export function hasPermission(
  permissions: PermissionsMap,
  permissionKey: string,
  health: Health | null = null
): boolean {
  if (!permissions || typeof permissions !== 'object') {
    return false
  }

  const hasPerm = permissions[permissionKey] === true

  if (health) {
    return hasPerm && health.canWrite === true
  }

  return hasPerm
}

/**
 * Get which permissions a user has for a specific feature
 * @param permissions - The permissions object from getPlacePermissions()
 * @param featureKey - The key from PLACE_PERMISSIONS_MAP (e.g., 'ADD_PLACE', 'EDIT_FACT')
 * @return Object with permission keys as keys and boolean values
 */
export function getFeaturePermissions(permissions: PermissionsMap, featureKey: string): Record<string, boolean> {
  const featurePermissions = PLACE_PERMISSIONS_MAP[featureKey as keyof typeof PLACE_PERMISSIONS_MAP]
  if (!featurePermissions) {
    debug(`Feature '${featureKey}' not found in PLACE_PERMISSIONS_MAP`)
    return {}
  }

  const result: Record<string, boolean> = {}
  Object.entries(featurePermissions).forEach(([key, permissionValue]) => {
    result[key] = permissions[permissionValue] === true
  })

  return result
}

/**
 * Check if user can perform edit mode for a feature (allows partial permissions)
 * @param permissions - The permissions object from getPlacePermissions()
 * @param featureKey - The key from PLACE_PERMISSIONS_MAP (e.g., 'ADD_PLACE', 'EDIT_FACT')
 * @param health - Health object with canWrite property, optional for permission checks
 * @return True if user can edit (has any permission for feature and canWrite if health provided)
 */
export function canEdit(permissions: PermissionsMap, featureKey: string, health: Health | null = null): boolean {
  return hasAnyPermissionForFeature(permissions, featureKey, health)
}

/**
 * Check if user can perform all edit operations for a feature
 * @param permissions - The permissions object from getPlacePermissions()
 * @param featureKey - The key from PLACE_PERMISSIONS_MAP (e.g., 'ADD_PLACE', 'EDIT_FACT')
 * @param health - Health object with canWrite property, optional for permission checks
 * @return True if user can edit all aspects (has all permissions for feature and canWrite if health provided)
 */
export function canEditAll(permissions: PermissionsMap, featureKey: string, health: Health | null = null): boolean {
  return hasAllPermissionsForFeature(permissions, featureKey, health)
}

/**
 * Check if user can add/create for a feature (requires all permissions)
 * @param permissions - The permissions object from getPlacePermissions()
 * @param featureKey - The key from PLACE_PERMISSIONS_MAP (e.g., 'ADD_PLACE', 'EDIT_FACT')
 * @param health - Health object with canWrite property, optional for permission checks
 * @return True if user can add (has all permissions for add feature and canWrite if health provided)
 */
export function canAdd(permissions: PermissionsMap, featureKey: string, health: Health | null = null): boolean {
  return hasAllPermissionsForFeature(permissions, featureKey, health)
}

/**
 * Check if user can delete for a feature (requires all permissions)
 * @param permissions - The permissions object from getPlacePermissions()
 * @param featureKey - The key from PLACE_PERMISSIONS_MAP (e.g., 'ADD_PLACE', 'EDIT_FACT')
 * @param health - Health object with canWrite property, optional for permission checks
 * @return True if user can delete (has all permissions for delete feature and canWrite if health provided)
 */
export function canDelete(permissions: PermissionsMap, featureKey: string, health: Health | null = null): boolean {
  return hasAllPermissionsForFeature(permissions, featureKey, health)
}

/**
 * Check if user can perform a specific action within a feature
 * @param permissions - The permissions object from getPlacePermissions()
 * @param featureKey - The key from PLACE_PERMISSIONS_MAP (e.g., 'ADD_PLACE', 'EDIT_FACT')
 * @param actionKey - The specific action key within the feature
 * @param health - Health object with canWrite property, optional for permission checks
 * @return True if user has the specific permission
 */
export function canPerformAction(
  permissions: PermissionsMap,
  featureKey: string,
  actionKey: string,
  health: Health | null = null
): boolean {
  const featurePermissions = PLACE_PERMISSIONS_MAP[featureKey as keyof typeof PLACE_PERMISSIONS_MAP]
  if (!featurePermissions?.[actionKey as keyof typeof featurePermissions]) {
    debug(`Action '${actionKey}' not found in feature '${featureKey}'`)
    return false
  }

  return hasPermission(permissions, featurePermissions[actionKey as keyof typeof featurePermissions], health)
}

// Certainty level constants
export const CERTAINTY_LEVELS = {
  PROVISIONAL: 0,
  ACCEPTED: 1,
  CERTIFIED: 2,
} as const

/**
 * Get allowed certainty transitions based on current state and permissions
 * @param permissions - The permissions object from getPlacePermissions()
 * @param currentCertainty - Current certainty value (0=provisional, 1=accepted, 2=certified)
 * @param health - Health object with canWrite property
 * @returns Array of allowed certainty values to transition to
 */
export function getAllowedCertaintyTransitions(
  permissions: PermissionsMap,
  currentCertainty: number,
  health: Health | null = null
): number[] {
  const transitions: number[] = []
  // Default health to allow writing if not provided
  const effectiveHealth = health || { canWrite: true }

  // Always allow staying in current state
  transitions.push(currentCertainty)

  // Check transitions based on current state
  if (currentCertainty === CERTAINTY_LEVELS.PROVISIONAL) {
    if (
      hasPermission(permissions, PLACE_PERMISSIONS_MAP.CERTIFICATION.PERM_REP_PROVISIONAL_TO_CONFIRMED, effectiveHealth)
    ) {
      transitions.push(CERTAINTY_LEVELS.ACCEPTED)
    }
  } else if (currentCertainty === CERTAINTY_LEVELS.ACCEPTED) {
    if (
      hasPermission(permissions, PLACE_PERMISSIONS_MAP.CERTIFICATION.PERM_REP_CONFIRMED_TO_PROVISIONAL, effectiveHealth)
    ) {
      transitions.push(CERTAINTY_LEVELS.PROVISIONAL)
    }
    if (
      hasPermission(permissions, PLACE_PERMISSIONS_MAP.CERTIFICATION.PERM_REP_CONFIRMED_TO_CERTIFIED, effectiveHealth)
    ) {
      transitions.push(CERTAINTY_LEVELS.CERTIFIED)
    }
  } else if (currentCertainty === CERTAINTY_LEVELS.CERTIFIED) {
    if (
      hasPermission(permissions, PLACE_PERMISSIONS_MAP.CERTIFICATION.PERM_REP_CERTIFIED_TO_CONFIRMED, effectiveHealth)
    ) {
      transitions.push(CERTAINTY_LEVELS.ACCEPTED)
    }
  }

  return Array.from(new Set(transitions))
}
